import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MailCheck, Loader2, TriangleAlert, RefreshCw } from "lucide-react";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { useToast } from "../hooks/use-toast";

const CodeForm = ({ onDone }) => {
  const { t } = useLang();
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/auth/verify-email/confirm", { code: code.trim(), email: email.trim() || undefined });
      onDone();
    } catch (ex) {
      setErr(ex.response?.data?.detail || t("ver.badCode"));
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="mt-5 rounded-2xl border border-border bg-secondary/40 p-4" data-testid="verify-page-code-form">
      <p className="text-sm font-bold text-foreground">{t("ver.enterCodeHere")}</p>
      {!user && (
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
          placeholder={t("ver.emailPlaceholder")} data-testid="verify-page-code-email"
          className="mt-3 w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm" />
      )}
      <div className="mt-3 flex flex-col sm:flex-row gap-2 justify-center">
        <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          inputMode="numeric" autoFocus placeholder={t("ver.codePlaceholder")} data-testid="verify-page-code-input"
          className="w-full sm:w-40 rounded-xl border border-border bg-background px-3 py-2.5 text-sm font-bold tracking-[0.25em] text-center" />
        <button type="submit" disabled={code.length !== 6 || (!user && !email.trim()) || busy} data-testid="verify-page-code-btn"
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white text-sm font-bold px-5 py-2.5 transition-colors">
          {busy && <Loader2 className="animate-spin" size={15} />} {t("ver.confirmCode")}
        </button>
      </div>
      {err && <p className="mt-2 text-xs text-rose-600" data-testid="verify-page-code-error">{err}</p>}
    </form>
  );
};

const ResendForm = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const { toast } = useToast();
  const [email, setEmail] = useState(user?.email || "");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/verify-email/resend", { email: email.trim() });
      setSent(true);
      toast({ title: t("ver.sent"), description: t("ver.sentNote", { email: email.trim() }) });
    } catch (ex) {
      toast({ title: t("ver.couldNotSend"), description: ex.response?.data?.detail || "" });
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="mt-4 text-left" data-testid="verify-page-resend-form">
      <p className="text-xs font-bold text-muted-foreground text-center">{t("ver.needANewOne")}</p>
      <div className="mt-2 flex flex-col sm:flex-row gap-2">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
          placeholder={t("ver.emailPlaceholder")} data-testid="verify-page-resend-input"
          className="flex-1 rounded-xl border border-border bg-background px-3 py-2.5 text-sm" />
        <button type="submit" disabled={busy || sent} data-testid="verify-page-resend-btn"
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-border hover:border-[#16a34a] disabled:opacity-60 text-sm font-bold px-4 py-2.5 transition-colors">
          {busy ? <Loader2 className="animate-spin" size={15} /> : <RefreshCw size={14} />}
          {sent ? t("ver.emailSentShort") : t("ver.sendNewEmail")}
        </button>
      </div>
    </form>
  );
};

const VerifyEmail = () => {
  const { t } = useLang();
  const [params] = useSearchParams();
  const { user, refresh } = useAuth();
  const [state, setState] = useState(params.get("token") ? "working" : "manual");
  const [error, setError] = useState("");

  const finish = async () => { setState("done"); if (user) await refresh(); };

  useEffect(() => {
    const token = params.get("token");
    if (!token) return;
    api.post("/auth/verify-email/confirm", { token })
      .then(finish)
      .catch((err) => { setState("bad"); setError(err.response?.data?.detail || ""); });
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-lg mx-auto py-14" data-testid="verify-email-page">
      <div className="bg-card border border-border rounded-3xl p-8 text-center">
        {state === "working" && (
          <>
            <Loader2 className="animate-spin mx-auto text-[#16a34a]" size={30} />
            <p className="mt-4 font-bold text-foreground">{t("ver.checking")}</p>
          </>
        )}
        {state === "done" && (
          <>
            <span className="h-14 w-14 mx-auto rounded-2xl bg-[#e7f6ec] dark:bg-[#16341f] flex items-center justify-center">
              <MailCheck size={26} className="text-[#16a34a]" />
            </span>
            <h1 className="mt-4 text-2xl font-extrabold text-foreground">{t("ver.done")}</h1>
            <p className="mt-2 text-sm text-muted-foreground">{t("ver.doneNote")}</p>
            <Link to="/account" data-testid="verify-done-cta"
              className="mt-6 inline-flex items-center justify-center rounded-xl bg-[#16a34a] hover:bg-[#15803d] text-white font-bold px-5 py-3 transition-colors">
              {t("ver.goToAccount")}
            </Link>
          </>
        )}
        {(state === "bad" || state === "manual") && (
          <>
            {state === "bad" ? (
              <>
                <span className="h-14 w-14 mx-auto rounded-2xl bg-rose-100 dark:bg-rose-950/40 flex items-center justify-center">
                  <TriangleAlert size={26} className="text-rose-600" />
                </span>
                <h1 className="mt-4 text-2xl font-extrabold text-foreground">{t("ver.badLink")}</h1>
                <p className="mt-2 text-sm text-muted-foreground" data-testid="verify-error">{error || t("ver.badLinkNote")}</p>
              </>
            ) : (
              <>
                <span className="h-14 w-14 mx-auto rounded-2xl bg-[#e7f6ec] dark:bg-[#16341f] flex items-center justify-center">
                  <MailCheck size={26} className="text-[#16a34a]" />
                </span>
                <h1 className="mt-4 text-2xl font-extrabold text-foreground">{t("ver.manualTitle")}</h1>
                <p className="mt-2 text-sm text-muted-foreground">{t("ver.manualNote")}</p>
              </>
            )}
            <CodeForm onDone={finish} />
            <ResendForm />
            <Link to="/account" data-testid="verify-bad-cta"
              className="mt-4 inline-flex items-center justify-center text-sm font-bold text-muted-foreground hover:text-foreground px-5 py-2.5">
              {t("ver.goToAccount")}
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
